/**
 * Demographic Widget (Story 5.19, Task 3 → Epic 37 redesign on shadcn/ui charts)
 *
 * Displays audience demographic data in 4 visualization modes:
 * - Device Breakdown: donut chart of device types
 * - New vs Returning: donut chart of visitor segments
 * - Geographic Top Regions: horizontal bar chart of top regions
 * - Age & Gender: grouped bar chart by age bracket and gender
 *
 * Rendered with shadcn's ChartContainer (Recharts v3, height-enforced so charts
 * actually render — the old height:100% in a flex parent measured 0 in the scaled
 * preview frame, leaving only the legend). Chrome (title / padding / states) comes
 * from the shared chart-widget shell. Navy monochrome series per design direction;
 * the age-gender mode keeps MEANINGFUL semantic gender colors. Clean axes (no
 * spines, faint horizontal grid). Anim off for PDF parity.
 *
 * Progressive degradation by colSpan, AND the measured `compact` flag (short box /
 * preview frame) collapses to the compact tier so charts never overlap themselves:
 * - ≤2: placeholder
 * - 3-4 (or compact): chart only, no legend/secondary
 * - 5-6: standard (chart + legend + secondary metrics)
 * - 7+: full (all of above + comparison trends)
 */

import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";
import { ChartContainer } from "../../ui/chart";
import type { WidgetConfig } from "../../../lib/templateUtils";
import type { DemographicData, WidgetDataEnvelope } from "../../../lib/widgetData";
import { isDemographicData } from "../../../lib/widgetData";
import { calculateTrend, TREND_COLORS } from "@provenleap/types/report-rendering";
import { useChartWidgetShell, ChartMessage, NAVY_SERIES } from "./chartWidgetShell";

interface DemographicWidgetProps {
  widget: WidgetConfig;
  data?: WidgetDataEnvelope<DemographicData>;
}

type DemographicTier = "placeholder" | "compact" | "standard" | "full";

function getTier(colSpan: number): DemographicTier {
  if (colSpan <= 2) return "placeholder";
  if (colSpan <= 4) return "compact";
  if (colSpan <= 6) return "standard";
  return "full";
}

/** Format duration in seconds to mm:ss */
function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}

/** Recharts pie label renderer — shows "Name X%" outside compact tier */
function renderPieLabel(props: { name?: string; percentage?: number }): string {
  const name = props.name ?? "";
  const pct = Number(props.percentage ?? 0).toFixed(0);
  return `${name} ${pct}%`;
}

/** Faint horizontal-only grid + clean axes, matching BarChartWidget. */
const GRID_STROKE = "hsl(214 25% 70% / 0.18)";
const AXIS_TICK = { fontSize: 11, fill: "hsl(var(--muted-foreground))" } as const;
const AXIS_TICK_COMPACT = { fontSize: 9, fill: "hsl(var(--muted-foreground))" } as const;
const TOOLTIP_STYLE = { borderRadius: 10, border: "0.5px solid var(--hairline)", fontSize: 12 } as const;

/**
 * Semantic gender colors (male / female / other). These are MEANINGFUL — kept
 * distinct, modernized to sit in the navy/neutral-friendly palette: navy (male),
 * coral accent (female), mid-slate (other).
 */
const GENDER_COLORS: Record<string, string> = {
  male: "hsl(222 47% 34%)",
  female: "hsl(2 70% 62%)",
  other: "hsl(214 20% 58%)",
};

export function DemographicWidget({ widget, data }: DemographicWidgetProps) {
  const colSpan = widget.position.colSpan;
  const config = widget.config as Record<string, unknown>;
  const showTrend = config.showTrendIndicator !== false;
  const { compact, shell } = useChartWidgetShell(widget.config);

  // Loading state
  if (data?.loading) {
    return shell(
      "demographic",
      widget.title,
      <div className="h-full min-h-[60px] w-full animate-pulse rounded bg-muted" />,
    );
  }

  // Error state
  if (data?.error) {
    return shell(
      "demographic",
      widget.title,
      <ChartMessage role="alert">{data.error}</ChartMessage>,
      { centered: true },
    );
  }

  const demographicData = data?.data;

  // No data / type guard failure
  if (!demographicData || !isDemographicData(demographicData)) {
    return shell(
      "demographic",
      widget.title,
      <ChartMessage testId="demographic-no-data">No data available</ChartMessage>,
      { centered: true },
    );
  }

  // Unavailable state (Google Signals required, etc.)
  if (demographicData.unavailable) {
    return shell(
      "demographic",
      widget.title,
      <div className="flex flex-col items-center gap-2 px-4 text-center">
        <span className="text-sm text-muted-foreground" data-testid="demographic-unavailable">
          {demographicData.unavailable.reason === "google_signals_required"
            ? "Google Signals must be enabled in your GA4 property to see demographic data."
            : demographicData.unavailable.reason === "data_thresholded"
              ? "Demographic data is hidden due to low traffic thresholds."
              : "No demographic data available for this period."}
        </span>
        {demographicData.unavailable.helpUrl && (
          <a
            href={demographicData.unavailable.helpUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-primary hover:underline"
            data-testid="demographic-help-link"
          >
            Learn how to enable this
          </a>
        )}
      </div>,
      { centered: true },
    );
  }

  // The measured-compact frame (short box / preview) collapses to the compact tier
  // so chart + legend + labels never crowd. colSpan still drives the wider tiers.
  const tier = compact ? "compact" : getTier(colSpan);

  // Placeholder at very narrow width
  if (tier === "placeholder") {
    return shell(
      "demographic",
      widget.title,
      <ChartMessage testId="chart-placeholder">Widen widget to view chart</ChartMessage>,
      { centered: true },
    );
  }

  return shell(
    "demographic",
    widget.title,
    <div className="flex min-h-0 min-w-0 flex-1 flex-col">
      <DemographicChart data={demographicData} tier={tier} showTrend={showTrend} />
    </div>,
  );
}

interface DemographicChartProps {
  data: DemographicData;
  tier: DemographicTier;
  showTrend: boolean;
}

function DemographicChart({ data, tier, showTrend }: DemographicChartProps) {
  switch (data.mode) {
    case "device-breakdown":
    case "new-vs-returning":
      return <DonutMode data={data} tier={tier} showTrend={showTrend} />;
    case "geographic-regions":
      return <GeographicMode data={data} tier={tier} showTrend={showTrend} />;
    case "age-gender":
      return <AgeGenderMode data={data} tier={tier} showTrend={showTrend} />;
    default:
      return <span className="text-xs text-muted-foreground">Unknown mode</span>;
  }
}

// ── Donut Mode (Device Breakdown & New vs Returning) ────────────────

interface DonutModeProps {
  data: DemographicData;
  tier: DemographicTier;
  showTrend: boolean;
}

function DonutMode({ data, tier, showTrend }: DonutModeProps) {
  const slices = data.slices ?? [];
  if (slices.length === 0) return <span className="text-sm text-muted-foreground">No data</span>;

  const showLegend = tier !== "compact";
  const showSecondary = tier === "standard" || tier === "full";
  const showComparison = tier === "full" && showTrend && data.comparisonData;
  const legendLayout = tier === "full" ? "vertical" : "horizontal";
  const legendAlign = tier === "full" ? "right" : "center";
  const legendVerticalAlign = tier === "full" ? "middle" : "bottom";

  const modeLabel = data.mode === "device-breakdown" ? "device type" : "visitor type";

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div
        className="min-h-0 flex-1"
        role="img"
        aria-label={`Donut chart showing sessions by ${modeLabel}: ${slices.map(s => `${s.name} ${s.percentage.toFixed(1)}%`).join(", ")}`}
      >
        <ChartContainer config={{}} className="h-full w-full">
          <PieChart margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
            <Pie
              data={slices}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              outerRadius="80%"
              innerRadius="55%"
              paddingAngle={1.5}
              stroke="hsl(var(--background))"
              strokeWidth={2}
              isAnimationActive={false}
              label={tier !== "compact" ? renderPieLabel : false}
              labelLine={tier !== "compact"}
            >
              {slices.map((_entry, index) => (
                <Cell key={`cell-${index}`} fill={NAVY_SERIES[index % NAVY_SERIES.length]} />
              ))}
            </Pie>
            <Tooltip
              cursor={{ fill: "hsl(214 25% 70% / 0.10)" }}
              contentStyle={TOOLTIP_STYLE}
              formatter={(value: number | string | undefined) =>
                new Intl.NumberFormat("en-US").format(Number(value ?? 0))
              }
            />
            {showLegend && slices.length > 1 && (
              <Legend
                layout={legendLayout as "horizontal" | "vertical"}
                align={legendAlign as "center" | "right"}
                verticalAlign={legendVerticalAlign as "bottom" | "middle"}
                wrapperStyle={{ fontSize: "11px" }}
              />
            )}
          </PieChart>
        </ChartContainer>
      </div>

      {/* Secondary metrics row */}
      {showSecondary && slices.some(s => s.secondaryMetrics) && (
        <div className="mt-1 flex flex-wrap gap-3 px-1" data-testid="demographic-secondary-metrics">
          {slices.map((slice) => {
            const avg = slice.secondaryMetrics?.averageSessionDuration;
            const pages = slice.secondaryMetrics?.screenPageViewsPerSession;
            if (avg === undefined && pages === undefined) return null;
            return (
              <div key={slice.name} className="text-[11px] text-muted-foreground">
                <span className="font-medium text-foreground">{slice.name}:</span>
                {avg !== undefined && <span className="ml-1 tabular-nums">{formatDuration(avg)}</span>}
                {pages !== undefined && <span className="ml-1 tabular-nums">{pages.toFixed(1)} pg/s</span>}
              </div>
            );
          })}
        </div>
      )}

      {/* Comparison trends */}
      {showComparison && data.comparisonData?.slices && (
        <div className="mt-1 flex flex-wrap gap-2 px-1" data-testid="demographic-comparison">
          {slices.map((slice) => {
            const compSlice = data.comparisonData!.slices?.find(s => s.name === slice.name);
            if (!compSlice) return null;
            const trend = calculateTrend(slice.percentage, compSlice.percentage);
            if (trend.direction === "neutral") return null;
            return (
              <span
                key={slice.name}
                className="text-[11px] tabular-nums"
                style={{ color: trend.direction === "up" ? TREND_COLORS.up : TREND_COLORS.down }}
              >
                {slice.name}: {trend.direction === "up" ? "↑" : "↓"} {trend.percentage.toFixed(1)}pp
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Geographic Mode ─────────────────────────────────────────────────

interface GeographicModeProps {
  data: DemographicData;
  tier: DemographicTier;
  showTrend: boolean;
}

function GeographicMode({ data, tier, showTrend }: GeographicModeProps) {
  const slices = data.slices ?? [];
  if (slices.length === 0) return <span className="text-sm text-muted-foreground">No data</span>;

  const isCompact = tier === "compact";
  const showComparison = tier === "full" && showTrend && data.comparisonData;
  const barData = slices.map(s => ({
    name: s.name,
    sessions: s.value,
    percentage: s.percentage,
  }));

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div
        className="min-h-0 flex-1"
        role="img"
        aria-label={`Horizontal bar chart showing top ${slices.length} regions by sessions`}
      >
        <ChartContainer config={{}} className="h-full w-full">
          <BarChart
            data={barData}
            layout="vertical"
            margin={{ top: 5, right: isCompact ? 5 : 20, bottom: 5, left: 5 }}
          >
            {!isCompact && <CartesianGrid stroke={GRID_STROKE} horizontal={false} />}
            <XAxis
              type="number"
              tick={isCompact ? AXIS_TICK_COMPACT : AXIS_TICK}
              tickLine={false}
              axisLine={false}
              tickMargin={4}
            />
            <YAxis
              type="category"
              dataKey="name"
              tick={isCompact ? AXIS_TICK_COMPACT : AXIS_TICK}
              width={isCompact ? 40 : 80}
              tickLine={false}
              axisLine={false}
              tickMargin={4}
            />
            <Tooltip
              cursor={{ fill: "hsl(214 25% 70% / 0.10)" }}
              contentStyle={TOOLTIP_STYLE}
              formatter={(value: number | string | undefined) =>
                new Intl.NumberFormat("en-US").format(Number(value ?? 0))
              }
            />
            <Bar dataKey="sessions" fill={NAVY_SERIES[0]} radius={[0, 4, 4, 0]} isAnimationActive={false}>
              {barData.map((_entry, index) => (
                <Cell key={`cell-${index}`} fill={NAVY_SERIES[index % NAVY_SERIES.length]} />
              ))}
            </Bar>
          </BarChart>
        </ChartContainer>
      </div>

      {/* Comparison trends */}
      {showComparison && data.comparisonData?.slices && (
        <div className="mt-1 flex flex-wrap gap-2 px-1" data-testid="demographic-comparison">
          {slices.slice(0, 5).map((slice) => {
            const compSlice = data.comparisonData!.slices?.find(s => s.name === slice.name);
            if (!compSlice) return null;
            const trend = calculateTrend(slice.value, compSlice.value);
            if (trend.direction === "neutral") return null;
            return (
              <span
                key={slice.name}
                className="text-[11px] tabular-nums"
                style={{ color: trend.direction === "up" ? TREND_COLORS.up : TREND_COLORS.down }}
              >
                {slice.name}: {trend.direction === "up" ? "↑" : "↓"} {trend.percentage.toFixed(1)}%
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Age & Gender Mode ───────────────────────────────────────────────

interface AgeGenderModeProps {
  data: DemographicData;
  tier: DemographicTier;
  showTrend: boolean;
}

function AgeGenderMode({ data, tier, showTrend }: AgeGenderModeProps) {
  const categories = data.categories ?? [];
  if (categories.length === 0) return <span className="text-sm text-muted-foreground">No data</span>;

  const isCompact = tier === "compact";
  const showLegend = !isCompact;
  const showComparison = tier === "full" && showTrend && data.comparisonData;

  // Collect all unique segment names (genders)
  const segmentNames = Array.from(
    new Set(categories.flatMap(c => c.segments.map(s => s.name))),
  );

  // Transform categories into Recharts-compatible data
  const barData = categories.map(cat => {
    const entry: Record<string, string | number> = { bracket: cat.bracket };
    for (const seg of cat.segments) {
      entry[seg.name] = seg.value;
    }
    return entry;
  });

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div
        className="min-h-0 flex-1"
        role="img"
        aria-label={`Grouped bar chart showing sessions by age bracket and gender`}
      >
        <ChartContainer config={{}} className="h-full w-full">
          <BarChart data={barData} margin={{ top: 5, right: 5, bottom: 5, left: 0 }}>
            {!isCompact && <CartesianGrid stroke={GRID_STROKE} vertical={false} />}
            <XAxis
              dataKey="bracket"
              tick={isCompact ? AXIS_TICK_COMPACT : AXIS_TICK}
              tickLine={false}
              axisLine={false}
              tickMargin={6}
            />
            <YAxis
              tick={isCompact ? AXIS_TICK_COMPACT : AXIS_TICK}
              width={35}
              tickLine={false}
              axisLine={false}
              tickMargin={4}
            />
            <Tooltip cursor={{ fill: "hsl(214 25% 70% / 0.10)" }} contentStyle={TOOLTIP_STYLE} />
            {showLegend && <Legend wrapperStyle={{ fontSize: 11, paddingTop: 4 }} />}
            {segmentNames.map((name, index) => (
              <Bar
                key={name}
                dataKey={name}
                name={name.charAt(0).toUpperCase() + name.slice(1)}
                fill={GENDER_COLORS[name] ?? NAVY_SERIES[index % NAVY_SERIES.length]}
                radius={[4, 4, 0, 0]}
                isAnimationActive={false}
              />
            ))}
          </BarChart>
        </ChartContainer>
      </div>

      {/* Comparison trends */}
      {showComparison && data.comparisonData?.categories && (
        <div className="mt-1 flex flex-wrap gap-2 px-1" data-testid="demographic-comparison">
          {segmentNames.map(name => {
            const currentTotal = categories.reduce(
              (sum, cat) => sum + (cat.segments.find(s => s.name === name)?.value ?? 0), 0,
            );
            const compTotal = data.comparisonData!.categories!.reduce(
              (sum, cat) => sum + (cat.segments.find(s => s.name === name)?.value ?? 0), 0,
            );
            const trend = calculateTrend(currentTotal, compTotal);
            if (trend.direction === "neutral") return null;
            return (
              <span
                key={name}
                className="text-[11px] tabular-nums"
                style={{ color: trend.direction === "up" ? TREND_COLORS.up : TREND_COLORS.down }}
              >
                {name}: {trend.direction === "up" ? "↑" : "↓"} {trend.percentage.toFixed(1)}%
              </span>
            );
          })}
        </div>
      )}

      {/* Disclaimer */}
      {data.disclaimer && (
        <p className="mt-1 px-1 text-[10px] text-muted-foreground" data-testid="demographic-disclaimer">
          {data.disclaimer}
        </p>
      )}
    </div>
  );
}
